/**
 * HelpIcon Component
 * Displays an info icon that triggers documentation modal when clicked
 */

import React, { useState, useEffect } from 'react';
import { FiInfo, FiHelpCircle } from 'react-icons/fi';
import documentationService from '../../services/DocumentationService';
import DocumentationModal from './DocumentationModal';

const HelpIcon = ({
    documentationId,
    size = 16,
    className = '',
    variant = 'info', // 'info' or 'help'
    position = 'top-right' // position relative to parent
}) => {
    const [showModal, setShowModal] = useState(false);
    const [hasDocumentation, setHasDocumentation] = useState(false);
    const [isServiceInitialized, setIsServiceInitialized] = useState(false);

    useEffect(() => {
        // Initialize documentation service if not already done
        const initializeService = async () => {
            if (!documentationService.initialized) {
                await documentationService.initialize();
            }
            setIsServiceInitialized(true);
            setHasDocumentation(documentationService.hasDocumentation(documentationId));
        };

        initializeService();
    }, [documentationId]);

    const handleClick = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (hasDocumentation) {
            setShowModal(true);
            documentationService.trackUsage(documentationId, 'quickHelp');
        } else {
            console.warn(`No documentation found for ID: ${documentationId}`);
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
    };

    // Don't render if service isn't initialized or no documentation exists
    if (!isServiceInitialized || !hasDocumentation) {
        return null;
    }

    const Icon = variant === 'help' ? FiHelpCircle : FiInfo;

    const baseClasses = `
    inline-flex items-center justify-center
    text-gray-500 hover:text-blue-600
    transition-colors duration-200
    cursor-pointer
    rounded-full
    hover:bg-blue-50
    ${className}
  `.trim();

    const positionClasses = {
        'top-right': 'absolute top-1 right-1',
        'top-left': 'absolute top-1 left-1',
        'bottom-right': 'absolute bottom-1 right-1',
        'bottom-left': 'absolute bottom-1 left-1',
        'inline': '',
        'relative': 'relative',
    };

    return (
        <>
            <button
                onClick={handleClick}
                className={`${baseClasses} ${positionClasses[position]}`}
                style={{
                    width: size + 8,
                    height: size + 8,
                    minWidth: size + 8,
                    minHeight: size + 8
                }}
                title={`Get help about this feature`}
                aria-label={`Documentation for ${documentationId}`}
            >
                <Icon size={size} />
            </button>

            {showModal && (
                <DocumentationModal
                    documentationId={documentationId}
                    onClose={handleCloseModal}
                />
            )}
        </>
    );
};

export default HelpIcon; 